import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from 'asset/styles/login/login.module.scss';
import { useUserDetail } from 'context/userDetailContext';
import { ILoginApiPayload } from 'models/axiosModel';
import { agent } from 'apiClient/api';
import { useSnackbar } from 'context/snackbarContext';
import {validateEmailHelper} from "util/helpers";
import {LoginCard, LoginFooter, LoginLeftPartContent} from "component/login/loginComponents";

export type TLoginActionType = 'email' | 'phone';

export interface IFormErrors {
    user_email?: string;
    user_mobile_number?: string;
    user_password?: string;
}

const defaultFormDataValue: ILoginApiPayload = {
    user_email: '',
    user_mobile_number: '',
    user_password: '',
}

const Login: React.FC = () => {

    const navigate = useNavigate();

    const { userDetail, setUserDetail } = useUserDetail();
    const { showError, showSuccess } = useSnackbar();

    if (userDetail !== null) {
        navigate('/quotation');
    }

    const [activeTab, setActiveTab] = useState<TLoginActionType>('email');
    const [formData, setFormData] = useState<ILoginApiPayload>(defaultFormDataValue);
    const [errors, setErrors] = useState<IFormErrors>({});

    const handleTabChange = (tab: TLoginActionType): void => {
        setActiveTab(tab);
        setErrors({});
    };

    const validateForm = (): boolean => {
        const newErrors: IFormErrors = {};

        if (activeTab === "email") {
            if (!formData.user_email) {
                newErrors.user_email = 'Email is required';
            } else if (!validateEmailHelper(formData.user_email)) {
                newErrors.user_email = 'Please enter a valid email address';
            }
        } else {
            if (!formData.user_mobile_number) {
                newErrors.user_mobile_number = 'Phone number is required';
            } else if (formData.user_mobile_number.length !== 10) {
                newErrors.user_mobile_number = 'Please enter a valid 10 digit phone number';
            }
        }

        if (!formData.user_password) {
            newErrors.user_password = 'Password is required';
        } else if (formData.user_password.length < 6) {
            newErrors.user_password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        if (errors[name as keyof IFormErrors]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
        setFormData(prev => ({ ...prev, user_mobile_number: digitsOnly }));

        if (errors.user_mobile_number) {
            setErrors(prev => ({ ...prev, user_mobile_number: undefined }));
        }
    };

    const LoginAPI = async (): Promise<void> => {
        try {
            const payload: ILoginApiPayload = activeTab === 'email'
                ? { user_email: formData.user_email, user_password: formData.user_password }
                : { user_mobile_number: formData.user_mobile_number, user_password: formData.user_password };
            const response = await agent.LoginAPI.post(payload);
            if (response?.status === false) {
                showError(response?.error?.message || 'Login Failed! Please try again.');
            } else {
                showSuccess('Login successful');
                navigate('/quotation');
                setUserDetail(response?.data[0]);
            }
        } catch (error) {
            console.log("LoginAPI Error", error);
            showError('Login Failed! Please try again.');
        }
    }

    const handleSubmit = async (): Promise<void> => {
        if (!validateForm()) {
            return;
        }
        await LoginAPI();
    };

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginLeft}>
                <LoginLeftPartContent />
            </div>

            <div className={styles.loginRight}>
                <LoginCard
                    activeTab={activeTab}
                    formData={formData}
                    errors={errors}
                    handleTabChange={handleTabChange}
                    handleInputChange={handleInputChange}
                    handlePhoneChange={handlePhoneChange}
                    handleSubmit={handleSubmit}
                />
                <LoginFooter />
            </div>
        </div>
    );
};

export default Login;
